import { useContext } from "react";
import { useInAppWindowFocusOptional } from "@/lib/inAppWindowFocus";
import { ProjectOverviewContext } from "@/projectOverview/projectOverviewContextValue";
import {
  PROJECT_OVERVIEW_NESTED_OVERLAY_Z,
  PROJECT_OVERVIEW_OVERLAY_Z,
} from "@/projectOverview/projectOverviewLayers";

/**
 * Über Stundennachweis-Flow (z-[230]–z-[270]), damit z. B. Rechnungen
 * aus dem Stundennachweis nicht darunter liegen.
 *
 * Über Projektübersicht (z-[310]), nur wenn der Dokumenteneditor zuletzt
 * fokussiert wurde (z. B. App-Dokument aus der Übersicht):
 *   editor 320 → overlay 330 → nested 340 → aga 350 → agaOverlay 360
 *   → ab 370 → abNested 380
 *
 * Öffnet man die Projektübersicht aus dem Editor, bleibt der Editor auf dem
 * Standard-Stack – die Übersicht (z-310) liegt dann oben.
 */
export const DOCUMENT_EDITOR_Z = "z-[280]";

/** Kopfdaten, Position, Vorschau, Mail usw. — über dem Editor-Modal. */
export const DOCUMENT_EDITOR_OVERLAY_Z = "z-[290]";

/** Verschachtelte Dialoge (z. B. Unterposition-Kalkulation in zusammengefasster Position). */
export const DOCUMENT_EDITOR_NESTED_OVERLAY_Z = "z-[300]";

/** Angebotsanfrage über dem Dokumenteneditor (Angebot schreiben). */
export const AGA_OVER_DOCUMENT_EDITOR_Z = "z-[310]";

/** Nested Modals (MA, Confirm) über der Angebotsanfrage im Editor. */
export const AGA_OVER_DOCUMENT_EDITOR_OVERLAY_Z = "z-[320]";

/**
 * Bestellnummer / AB-aus-Angebot — über Editor und AGA, damit Angenommen
 * und Beauftragung-Upload das Modal nicht hinter dem Dokumenteneditor öffnen.
 */
export const AB_FROM_ANGEBOT_Z = "z-[330]";

/** Positionsauswahl über dem Bestellnummer-Dialog. */
export const AB_FROM_ANGEBOT_NESTED_Z = "z-[340]";

export interface DocumentEditorLayers {
  editor: string;
  overlay: string;
  nested: string;
  aga: string;
  agaOverlay: string;
  ab: string;
  abNested: string;
}

const DEFAULT_LAYERS: DocumentEditorLayers = {
  editor: DOCUMENT_EDITOR_Z,
  overlay: DOCUMENT_EDITOR_OVERLAY_Z,
  nested: DOCUMENT_EDITOR_NESTED_OVERLAY_Z,
  aga: AGA_OVER_DOCUMENT_EDITOR_Z,
  agaOverlay: AGA_OVER_DOCUMENT_EDITOR_OVERLAY_Z,
  ab: AB_FROM_ANGEBOT_Z,
  abNested: AB_FROM_ANGEBOT_NESTED_Z,
};

/** Über Projektübersicht (310) und deren Overlays (320/330). */
const OVER_OVERVIEW_LAYERS: DocumentEditorLayers = {
  editor: PROJECT_OVERVIEW_OVERLAY_Z, // z-[320]
  overlay: PROJECT_OVERVIEW_NESTED_OVERLAY_Z, // z-[330]
  nested: "z-[340]",
  aga: "z-[350]",
  agaOverlay: "z-[360]",
  ab: "z-[370]",
  abNested: "z-[380]",
};

/**
 * Z-Index-Stack für Dokumenteneditor inkl. Overlays.
 * Anhebung nur wenn der Editor zuletzt fokussiert wurde und die
 * Projektübersicht offen ist (analog `useTimesheetFlowLayers`).
 */
export function useDocumentEditorLayers(): DocumentEditorLayers {
  const projectOverviewOpen = useContext(ProjectOverviewContext)?.isOpen ?? false;
  const frontSurface = useInAppWindowFocusOptional()?.frontSurface ?? null;
  const documentEditorIsFront = frontSurface === "documentEditor";

  if (documentEditorIsFront && projectOverviewOpen) {
    return OVER_OVERVIEW_LAYERS;
  }
  return DEFAULT_LAYERS;
}
